import {
  Anchor,
  Button,
  Flex,
  Group,
  Menu,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import {
  IconArrowBadgeUpFilled,
  IconChevronDown,
  IconDeviceMobileMessage,
  IconFileImport,
  IconMessage,
  IconMicrophoneFilled,
  IconPlus,
  IconUserPlus,
} from "@tabler/icons-react";
import { Link, useParams } from "react-router-dom";
import { getModelTypeProps } from "./modelUtils";
import { useLanguage } from "../../../stores/LanguageStore";
import { useProfileStore } from "../../../stores/UserStore";
import { useMemo } from "react";
import { modals } from "@mantine/modals";
import { getPermission } from "../../../functions/utilFunctions";
import UpgradeIllustration from "../../../layouts/ErrorModal/UpgradeIllustration";
import { IconEmail } from "../../../assets/icons/IconEmail";
import LinkedinVoiceRecord from "./linkedin/LinkedinVoiceRecord";
import LinkedinVoiceFile from "./linkedin/LinkedinVoiceFile";

const iconStyles = { size: 20 };

const ModalContent = () => {
  const lang = useLanguage((s) => s.language);

  const handleClose = () => {
    modals.close("not-allowed");
  };

  return (
    <Stack>
      <Text c={"red"} size="xl" ta={"center"} pl={32}>
        {lang.model_invitation_linkedin.modal_error.text}
      </Text>

      {getPermission("displayPricing") && (
        <Group wrap="nowrap" pl={32}>
          <Stack gap={"md"}>
            <Text size="lg">{lang.errors.modal.not_allowed}</Text>

            <Group>
              <Anchor href={"/settings/subscription"} target="_blank">
                <Button
                  size="md"
                  leftSection={
                    <ThemeIcon variant={"subtle"} size={28} color={"#fcfbff"}>
                      <IconArrowBadgeUpFilled size={20} />
                    </ThemeIcon>
                  }
                  onClick={handleClose}
                >
                  {lang.errors.modal.upgrade}
                </Button>
              </Anchor>
            </Group>
          </Stack>

          <Flex w={300}>
            <UpgradeIllustration />
          </Flex>
        </Group>
      )}
    </Stack>
  );
};

const CreateModel = ({ inTable, variant = "filled", handleTableLoad }) => {
  const profile = useProfileStore((s) => s.profile);
  const lang = useLanguage((s) => s.language);
  const { folderId, modelType } = useParams();

  const allowed = useMemo(() => {
    return modelType === "email"
      ? true
      : profile?.permissions?.find(
            (x) => x.name === getModelTypeProps(modelType)?.create_permission,
          )
        ? profile?.permissions?.find(
            (x) => x.name === getModelTypeProps(modelType)?.create_permission,
          )?.value === true
        : true;
  }, [modelType, profile?.permissions]);

  const handleModal = () => {
    modals.open({
      modalId: "not-allowed",
      children: <ModalContent />,
      size: "lg",
    });
  };

  const handleVocalRecord = () => {
    modals.open({
      modalId: "linkedin-voice-message",
      title: lang.models.linkedin_vocal.modal_record_title,
      children: (
        <LinkedinVoiceRecord
          handleClose={() => modals.close("linkedin-voice-message")}
          onCreated={handleTableLoad}
        />
      ),
      size: "xl",
    });
  };

  const handleVocalFile = () => {
    modals.open({
      modalId: "linkedin-voice-file",
      title: lang.models.linkedin_vocal.modal_import_title,
      size: "xl",
      children: (
        <LinkedinVoiceFile
          handleClose={() => modals.close("linkedin-voice-file")}
          onCreated={handleTableLoad}
        />
      ),
    });
  };

  return modelType === "linkedin-voice-message" ? (
    <Menu shadow="md" width={200} position="bottom">
      <Menu.Target>
        <Button
          leftSection={<IconPlus size={18} />}
          size="md"
          radius={"md"}
          fz={16}
          variant={variant}
          color="primary"
          rightSection={<IconChevronDown size={18} />}
        >
          {lang.models.linkedin_vocal.btn_create}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        {profile.permissions?.find(
          (x) =>
            x.name === getModelTypeProps("linkedin-message")?.create_permission,
        )?.value === true && (
          <Menu.Item
            leftSection={<IconMicrophoneFilled {...iconStyles} />}
            onClick={handleVocalRecord}
            fz={14}
          >
            {lang.models.linkedin_vocal.menu_record}
          </Menu.Item>
        )}
        <Menu.Divider />
        {profile.permissions?.find(
          (x) =>
            x.name === getModelTypeProps("linkedin-message")?.create_permission,
        )?.value === true && (
          <Menu.Item
            leftSection={<IconFileImport {...iconStyles} />}
            onClick={handleVocalFile}
            fz={14}
          >
            {lang.models.linkedin_vocal.menu_import}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  ) : inTable ? (
    <Button
      component={allowed ? Link : undefined}
      to={
        folderId
          ? `/models/folder/${folderId}/create`
          : `/models/${modelType}/create`
      }
      leftSection={<IconPlus size={32} />}
      radius={"md"}
      variant="light"
      color="tertiary.8"
      size="xl"
      onClick={allowed ? undefined : handleModal}
    >
      {lang.models.create_an} {getModelTypeProps(modelType)?.name}
    </Button>
  ) : folderId ? (
    <Menu shadow="md" width={200} position="bottom">
      <Menu.Target>
        <Button
          leftSection={<IconPlus size={18} />}
          size="md"
          radius={"md"}
          fz={16}
          variant={variant}
          color="primary"
          rightSection={<IconChevronDown size={18} />}
        >
          {lang.models.create_model}
        </Button>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Item
          leftSection={<IconEmail {...iconStyles} />}
          component={Link}
          to={`/models/folder/${folderId}/create/email`}
          fz={14}
        >
          {lang.routes.models.email}
        </Menu.Item>

        {profile.permissions?.find(
          (x) =>
            x.name ===
            getModelTypeProps("linkedin-invitation")?.create_permission,
        )?.value === true && (
          <Menu.Item
            leftSection={<IconUserPlus {...iconStyles} />}
            component={Link}
            to={`/models/folder/${folderId}/create/linkedin-invitation`}
            fz={14}
          >
            {lang.routes.models.linkedin_invitation}
          </Menu.Item>
        )}

        {profile.permissions?.find(
          (x) =>
            x.name === getModelTypeProps("linkedin-message")?.create_permission,
        )?.value === true && (
          <Menu.Item
            leftSection={<IconMessage {...iconStyles} />}
            component={Link}
            to={`/models/folder/${folderId}/create/linkedin-message`}
            fz={14}
          >
            {lang.routes.models.linkedin_message}
          </Menu.Item>
        )}

        {profile.permissions?.find(
          (x) => x.name === getModelTypeProps("sms")?.create_permission,
        )?.value === true && (
          <Menu.Item
            leftSection={<IconDeviceMobileMessage {...iconStyles} />}
            component={Link}
            to={`/models/folder/${folderId}/create/sms`}
            fz={14}
          >
            {lang.routes.models.sms}
          </Menu.Item>
        )}
      </Menu.Dropdown>
    </Menu>
  ) : (
    <Button
      component={allowed ? Link : undefined}
      to={
        folderId
          ? `/models/folder/${folderId}/create`
          : `/models/${modelType}/create`
      }
      leftSection={<IconPlus size={18} />}
      color="primary"
      onClick={allowed ? undefined : handleModal}
      variant={variant}
      size="md"
      radius={"md"}
      fz={16}
    >
      {lang.models.create_an} {getModelTypeProps(modelType)?.name}
    </Button>
  );
};

export default CreateModel;
